import { useMemo, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import ImageWithPreview from './ImageWithPreview';

// Fix Leaflet icons
if ('_getIconUrl' in L.Icon.Default.prototype) {
  delete (L.Icon.Default.prototype as any)._getIconUrl;
}
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Blue marker icon for coordinates
const blueIcon = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Red marker icon for selected screen
const redIcon = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [35, 51],
  iconAnchor: [17, 51],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Component to handle map zoom and pan when selected item changes
function MapController({
  selectedScreen,
}: {
  selectedScreen: { coordinate: [number, number] } | null;
}) {
  const map = useMap();

  useEffect(() => {
    if (selectedScreen?.coordinate) {
      // Zoom in and pan to the selected item
      map.setView(selectedScreen.coordinate, 16, {
        animate: true,
        duration: 0.5,
      });
    }
  }, [selectedScreen, map]);

  return null;
}

// Component for individual marker - internal to StreetMap
function MapMarker({ item }: { item: MapItem }) {
  const markerRef = useRef<L.Marker>(null);
  const position = item.coordinate;
  const isSelected = item.selected || false;

  useEffect(() => {
    if (isSelected && markerRef.current) {
      setTimeout(() => {
        markerRef.current?.openPopup();
      }, 100);
    }
  }, [isSelected, position]);

  // Handle marker click to open popup
  const handleMarkerClick = () => {
    if (markerRef.current) {
      markerRef.current.openPopup();
    }
  };

  const lat = position[0];
  const lon = position[1];

  // Extract display data from the item object
  const name = item?.name || item?.title || 'N/A';
  const price = item?.price
    ? `₹${item.price.toLocaleString('en-IN')}/month`
    : item?.priceLabel || 'N/A';
  const resolution = item?.resolution
    ? typeof item.resolution === 'string'
      ? item.resolution
      : `${item.resolution.width} × ${item.resolution.height}`
    : item?.size || 'N/A';
  const orientation = item?.orientation || 'N/A';
  const address =
    typeof item?.address === 'string'
      ? item.address
      : item?.address?.landmark || item?.address?.full || 'N/A';

  return (
    <Marker
      ref={markerRef}
      position={position}
      icon={isSelected ? redIcon : blueIcon}
      zIndexOffset={isSelected ? 1000 : 0}
      eventHandlers={{
        click: handleMarkerClick,
      }}
    >
      <Popup>
        <div style={{ minWidth: '250px', padding: '8px' }}>
          <div
            style={{
              fontWeight: 'bold',
              color: isSelected ? '#dc2626' : '#3b82f6',
              marginBottom: '8px',
              fontSize: '14px',
              borderBottom: `2px solid ${isSelected ? '#dc2626' : '#3b82f6'}`,
              paddingBottom: '6px',
            }}
          >
            {isSelected ? '⭐' : '📍'} {name}
          </div>

          {/* Images - display with preview icon */}
          {(() => {
            // Extract image URLs from deviceImage array
            const deviceImages =
              item?.deviceImage && Array.isArray(item.deviceImage)
                ? item.deviceImage.map((img: any) => img.url).filter(Boolean)
                : [];

            // Use deviceImages if available, otherwise use legacy images field, or default image
            const imagesToDisplay =
              deviceImages.length > 0
                ? deviceImages
                : item?.images && item.images.length > 0
                  ? item.images
                  : ['https://via.placeholder.com/300x200?text=No+Image'];

            return (
              <div style={{ marginBottom: '12px' }}>
                {imagesToDisplay.length === 1 ? (
                  <ImageWithPreview
                    src={imagesToDisplay[0]}
                    alt={name}
                    width="100%"
                    height="150px"
                    iconSize={28}
                  />
                ) : (
                  <div
                    style={{ display: 'flex', gap: '4px', overflowX: 'auto' }}
                  >
                    {imagesToDisplay.map((img: string, idx: number) => (
                      <ImageWithPreview
                        key={idx}
                        src={img}
                        alt={`${name} ${idx + 1}`}
                        width={imagesToDisplay.length > 2 ? '80px' : '120px'}
                        height="100px"
                        borderRadius="6px"
                        iconSize={20}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {price !== 'N/A' && (
            <div style={{ marginBottom: '6px' }}>
              <span
                style={{
                  fontWeight: '600',
                  color: '#6b7280',
                  fontSize: '12px',
                }}
              >
                Price:
              </span>
              <span
                style={{
                  color: '#059669',
                  fontWeight: '600',
                  marginLeft: '8px',
                  fontSize: '12px',
                }}
              >
                {price}
              </span>
            </div>
          )}
          {resolution !== 'N/A' && (
            <div style={{ marginBottom: '6px' }}>
              <span
                style={{
                  fontWeight: '600',
                  color: '#6b7280',
                  fontSize: '12px',
                }}
              >
                Size:
              </span>
              <span
                style={{
                  color: '#374151',
                  marginLeft: '8px',
                  fontSize: '12px',
                }}
              >
                {resolution}
              </span>
            </div>
          )}
          {orientation !== 'N/A' && (
            <div style={{ marginBottom: '6px' }}>
              <span
                style={{
                  fontWeight: '600',
                  color: '#6b7280',
                  fontSize: '12px',
                }}
              >
                Orientation:
              </span>
              <span
                style={{
                  color: '#374151',
                  marginLeft: '8px',
                  fontSize: '12px',
                }}
              >
                {orientation}
              </span>
            </div>
          )}
          {address !== 'N/A' && (
            <div style={{ marginBottom: '6px' }}>
              <span
                style={{
                  fontWeight: '600',
                  color: '#6b7280',
                  fontSize: '12px',
                }}
              >
                Address:
              </span>
              <div
                style={{
                  color: '#374151',
                  marginTop: '2px',
                  fontSize: '12px',
                  lineHeight: '1.4',
                }}
              >
                {address}
              </div>
            </div>
          )}
          <div
            style={{
              marginTop: '8px',
              paddingTop: '6px',
              borderTop: '1px solid #e5e7eb',
            }}
          >
            <div
              style={{
                fontSize: '11px',
                color: '#6b7280',
                fontFamily: 'monospace',
              }}
            >
              <div>Lat: {lat.toFixed(6)}</div>
              <div>Lng: {lon.toFixed(6)}</div>
            </div>
          </div>
        </div>
      </Popup>
    </Marker>
  );
}

// Type definition for map items
export interface MapItem {
  coordinate: [number, number];
  selected?: boolean;
  id?: string | number;
  name?: string;
  title?: string;
  price?: number;
  priceLabel?: string;
  resolution?: string | { width: number; height: number };
  size?: string;
  orientation?: string;
  address?: string | { landmark?: string; full?: string };
  [key: string]: any; // Allow additional properties
}

// --------------------------------------------------
// ⭐ MAIN COMPONENT - Fully Reusable
// --------------------------------------------------
export default function StreetMap({
  items = [],
  zoom = 14,
  center,
}: {
  items: MapItem[];
  zoom?: number;
  center?: [number, number];
}) {
  // Get center from first item or provided center or default
  const mapCenter: [number, number] =
    center ||
    (items.length > 0 && items[0]?.coordinate
      ? items[0].coordinate
      : [20.5937, 78.9629]);

  // Find selected item for auto-zoom
  const selectedItem = useMemo(() => {
    return items.find(item => item.selected) || null;
  }, [items]);

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        position: 'relative',
        minHeight: '300px',
      }}
    >
      <MapContainer
        zoom={zoom}
        style={{ height: '100%', width: '100%', minHeight: '300px' }}
        center={mapCenter}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        {/* Map controller to handle zoom and pan for selected item */}
        <MapController selectedScreen={selectedItem} />

        {/* Render all markers */}
        {items.length > 0 ? (
          items.map((item, index) => {
            if (!item.coordinate || item.coordinate.length !== 2) {
              console.warn('Invalid coordinate for item:', item);
              return null;
            }
            return (
              <MapMarker
                key={
                  item?.id ||
                  `item_${index}_${item.coordinate[0]}_${item.coordinate[1]}`
                }
                item={item}
              />
            );
          })
        ) : (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
              background: 'white',
              padding: '10px',
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            }}
          >
            No markers to display
          </div>
        )}
      </MapContainer>
    </div>
  );
}
